/* eslint-disable max-classes-per-file */
import { Vector3, Matrix } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';

/**
 * Vertex data structure that includes basis vectors and geodesic coordinates
 */
class GeodesicVertex {
    constructor(position, normal, right, up, geoCoord) {
        this.position = position;
        this.normal = normal;
        this.right = right;
        this.up = up;
        this.geoCoord = geoCoord || { u: 0, v: 0 };
    }
}

/**
 * Simple binary heap-based priority queue for Dijkstra's algorithm
 */
class PriorityQueue {
    constructor() {
        this.heap = [];
    }

    get size() {
        return this.heap.length;
    }

    isEmpty() {
        return this.size === 0;
    }

    // Get parent index
    // eslint-disable-next-line class-methods-use-this
    getParentIndex(i) {
        return Math.floor((i - 1) / 2);
    }

    // Get left child index
    // eslint-disable-next-line class-methods-use-this
    getLeftChildIndex(i) {
        return 2 * i + 1;
    }

    // Get right child index
    // eslint-disable-next-line class-methods-use-this
    getRightChildIndex(i) {
        return 2 * i + 2;
    }

    // Swap elements at indices i and j
    swap(i, j) {
        [this.heap[i], this.heap[j]] = [this.heap[j], this.heap[i]];
    }

    // Add element to the queue
    enqueue(vertexIdx, distance) {
        this.heap.push({ vertexIdx, distance });
        this.siftUp(this.size - 1);
        return this;
    }

    // Remove and return the minimum element
    dequeue() {
        if (this.isEmpty()) {
            return null;
        }

        const min = this.heap[0];
        const last = this.heap.pop();

        if (!this.isEmpty()) {
            this.heap[0] = last;
            this.siftDown(0);
        }

        return min;
    }

    // Bubble up element at index i
    siftUp(i) {
        let currentIndex = i;
        let parentIndex = this.getParentIndex(currentIndex);

        while (
            currentIndex > 0
            && this.heap[currentIndex].distance < this.heap[parentIndex].distance
        ) {
            this.swap(currentIndex, parentIndex);
            currentIndex = parentIndex;
            parentIndex = this.getParentIndex(currentIndex);
        }
    }

    // Bubble down element at index i
    siftDown(i) {
        const currentIndex = i;
        const leftChildIndex = this.getLeftChildIndex(currentIndex);
        const rightChildIndex = this.getRightChildIndex(currentIndex);
        let smallest = currentIndex;

        const size = this.size;

        if (
            leftChildIndex < size
            && this.heap[leftChildIndex].distance < this.heap[smallest].distance
        ) {
            smallest = leftChildIndex;
        }

        if (
            rightChildIndex < size
            && this.heap[rightChildIndex].distance < this.heap[smallest].distance
        ) {
            smallest = rightChildIndex;
        }

        if (smallest !== currentIndex) {
            this.swap(currentIndex, smallest);
            this.siftDown(smallest);
        }
    }
}

/**
 * Welds vertices by position to create a properly indexed mesh
 * @param {FloatArray} positions - original vertex positions
 * @param {IndicesArray} indices - original indices
 * @param {number} tolerance - position comparison tolerance
 * @returns {Object} welded mesh data
 */
function weldVertices(positions, indices, tolerance = 0.00000001) {
    const uniqueVertices = new Map();
    const newPositions = [];
    const newIndices = [];
    const oldToNewIndex = new Map();

    // Round to specific precision to handle floating-point issues.
    function getPositionKey(x, y, z) {
        const precision = 1 / tolerance;

        return `${Math.round(x * precision)},${Math.round(y * precision)},${Math.round(z * precision)}`;
    }

    // Process each vertex
    for (let i = 0; i < indices.length; i += 1) {
        const oldIndex = indices[i];

        const x = positions[oldIndex * 3 + 0];
        const y = positions[oldIndex * 3 + 1];
        const z = positions[oldIndex * 3 + 2];

        const posKey = getPositionKey(x, y, z);

        // Check if we've seen this position before
        if (!uniqueVertices.has(posKey)) {
            // New unique vertex
            const newIndex = newPositions.length / 3;

            uniqueVertices.set(posKey, newIndex);

            newPositions.push(x, y, z);

            oldToNewIndex.set(oldIndex, newIndex);
        } else {
            // Reuse existing vertex
            oldToNewIndex.set(oldIndex, uniqueVertices.get(posKey));
        }

        // Add to new indices array
        newIndices.push(oldToNewIndex.get(oldIndex));
    }

    return {
        positions: newPositions,
        indices: newIndices,
        oldToNewIndex,
        uniqueCount: uniqueVertices.keys().length,
    };
}

/**
 * Welds vertex attributes based on position welding results
 * @param {FloatArray} sourceAttributes - original vertex attributes array (like normals)
 * @param {Object} weldedMeshData - result from weldVertices function
 * @param {number} itemSize - number of components per vertex attribute (3 for normals, 2 for UVs)
 * @returns {FloatArray} welded attributes array
 */
function weldVertexAttributes(sourceAttributes, weldedMeshData, itemSize) {
    const { oldToNewIndex, positions } = weldedMeshData;
    const uniqueVertexCount = positions.length / 3;

    // Create new array for welded attributes
    const weldedAttributes = new Array(uniqueVertexCount * itemSize).fill(0);

    // Maps to track which vertices have been processed and how many are merged
    const processedVertices = new Map();
    const vertexCounts = new Map();

    // For each original vertex, accumulate its attributes to the welded vertex

    for (const [oldIdx, newIdx] of oldToNewIndex.entries()) {
        // Skip invalid indices
        if (oldIdx * itemSize >= sourceAttributes.length) {
            // eslint-disable-next-line no-continue
            continue;
        }

        // Increment count for this welded vertex
        vertexCounts.set(newIdx, (vertexCounts.get(newIdx) || 0) + 1);

        // Add this vertex's attribute values to the welded vertex
        for (let i = 0; i < itemSize; i += 1) {
            weldedAttributes[newIdx * itemSize + i] += sourceAttributes[oldIdx * itemSize + i];
        }

        processedVertices.set(newIdx, true);
    }

    // Normalize accumulated attributes (average them)

    for (const [newIdx, count] of vertexCounts.entries()) {
        for (let i = 0; i < itemSize; i += 1) {
            weldedAttributes[newIdx * itemSize + i] /= count;
        }
    }

    // For normals, we need to renormalize them
    if (itemSize === 3) {
        for (let i = 0; i < uniqueVertexCount; i += 1) {
            const nx = weldedAttributes[i * 3 + 0];
            const ny = weldedAttributes[i * 3 + 1];
            const nz = weldedAttributes[i * 3 + 2];

            const length = Math.sqrt(nx * nx + ny * ny + nz * nz);

            if (length > 0.0001) {
                weldedAttributes[i * 3 + 0] = nx / length;
                weldedAttributes[i * 3 + 1] = ny / length;
                weldedAttributes[i * 3 + 2] = nz / length;
            }
        }
    }

    return weldedAttributes;
}

/**
 * Process an edge for building topology.
 * @param {number} v1 - first vertex index.
 * @param {number} v2 - second vertex index.
 * @param {number} triangleId - triangle id.
 * @param {Map} edgeToTriangle - edge to triangle map.
 */
function processEdge(v1, v2, triangleId, edgeToTriangle) {
    // Create a unique key for the edge (always sort vertex indices).
    const edgeKey = v1 < v2 ? `${v1}-${v2}` : `${v2}-${v1}`;

    // Add triangle to edge map.
    if (!edgeToTriangle.has(edgeKey)) {
        edgeToTriangle.set(edgeKey, []);
    }

    edgeToTriangle.get(edgeKey).push(triangleId);
}

/**
 * Builds the topology information for the mesh (vertex adjacency, triangle adjacency).
 * @param {IndicesArray} indices - mesh indices.
 * @param {FloatArray} positions - mesh positions.
   @returns {Object} topology information.
 */
function buildMeshTopology(indices, positions) {
    // Maps vertex indices to adjacent vertices.
    const vertexAdjacency = new Map();

    // Maps vertex indices to adjacent triangles.
    const vertexToTriangle = new Map();

    // Maps triangles to adjacent triangles.
    const triangleAdjacency = new Map();

    // Maps edges (as string keys) to triangles.
    const edgeToTriangle = new Map();

    // Initialize vertex adjacency maps
    for (let i = 0; i < positions.length / 3; i += 1) {
        vertexAdjacency.set(i, new Set());
        vertexToTriangle.set(i, new Set());
    }

    // Process each triangle
    for (let i = 0; i < indices.length; i += 3) {
        const triangleId = i / 3;

        triangleAdjacency.set(triangleId, new Set());

        const idx1 = indices[i + 0];
        const idx2 = indices[i + 1];
        const idx3 = indices[i + 2];

        // Add vertices to each other's adjacency lists
        vertexAdjacency.get(idx1).add(idx2);
        vertexAdjacency.get(idx1).add(idx3);
        vertexAdjacency.get(idx2).add(idx1);
        vertexAdjacency.get(idx2).add(idx3);
        vertexAdjacency.get(idx3).add(idx1);
        vertexAdjacency.get(idx3).add(idx2);

        // Add triangle to vertex-triangle map
        vertexToTriangle.get(idx1).add(triangleId);
        vertexToTriangle.get(idx2).add(triangleId);
        vertexToTriangle.get(idx3).add(triangleId);

        // Process edges for triangle adjacency
        processEdge(idx1, idx2, triangleId, edgeToTriangle);
        processEdge(idx2, idx3, triangleId, edgeToTriangle);
        processEdge(idx3, idx1, triangleId, edgeToTriangle);
    }

    // Build triangle adjacency from edge information

    for (const triangles of edgeToTriangle.values()) {
        // Should be a watertight manifold mesh.
        if (triangles.length === 2) {
            const [t1, t2] = triangles;

            triangleAdjacency.get(t1).add(t2);
            triangleAdjacency.get(t2).add(t1);
        }
    }

    return {
        vertexAdjacency,
        vertexToTriangle,
        triangleAdjacency,
    };
}

/**
 * Computes barycentric coordinates of a point within a triangle.
 * @param {Vector3} p - point.
 * @param {Vector3} a - first triangle vertex.
 * @param {Vector3} b - second triangle vertex.
 * @param {Vector3} c - third triangle vertex.
 * @returns {Object} barycentric coordinates.
 */
function computeBarycentricCoordinates(p, a, b, c) {
    const v0 = b.subtract(a);
    const v1 = c.subtract(a);
    const v2 = p.subtract(a);

    const d00 = Vector3.Dot(v0, v0);
    const d01 = Vector3.Dot(v0, v1);
    const d11 = Vector3.Dot(v1, v1);
    const d20 = Vector3.Dot(v2, v0);
    const d21 = Vector3.Dot(v2, v1);

    const denom = d00 * d11 - d01 * d01;

    if (Math.abs(denom) < 0.0000000001) {
        // Degenerate case, assume center.
        return { u: 1 / 3, v: 1 / 3, w: 1 / 3 };
    }

    const v = (d11 * d20 - d01 * d21) / denom;
    const w = (d00 * d21 - d01 * d20) / denom;

    const u = 1.0 - v - w;

    return { u, v, w };
}

/**
 * Computes geodesic basis and coordinates by propagating from the hit point
 * @param {Vector3} hitPoint - hit point on the mesh
 * @param {number} hitFaceId - hit face id
 * @param {IndicesArray} indices - mesh indices
 * @param {FloatArray} positions - mesh positions
 * @param {FloatArray} normals - mesh normals
 * @param {Object} topology - mesh topology
 * @param {Vector3} initialRight - initial right basis vector
 * @param {Vector3} initialUp - initial up basis vector
 * @param {number} maxDistance - maximum geodesic distance
 * @returns {Object} arrays of vertex data and triangle distances
 */
function computeGeodesicBasis(
    hitPoint,
    hitFaceId,
    indices,
    positions,
    normals,
    topology,
    initialRight,
    initialUp,
    maxDistance,
) {
    const { vertexAdjacency } = topology;
    const vertexCount = positions.length / 3;
    const triangleCount = indices.length / 3;

    // Replace Maps with arrays
    const vertexGeoData = new Array(vertexCount);
    const vertexDistances = new Array(vertexCount).fill(Infinity);
    const triangleDistances = new Array(triangleCount).fill(Infinity);

    // Get the vertices of the hit triangle
    const v1Idx = indices[hitFaceId * 3 + 0];
    const v2Idx = indices[hitFaceId * 3 + 1];
    const v3Idx = indices[hitFaceId * 3 + 2];

    // Get positions of the hit triangle vertices
    const v1 = new Vector3(positions[v1Idx * 3], positions[v1Idx * 3 + 1], positions[v1Idx * 3 + 2]);
    const v2 = new Vector3(positions[v2Idx * 3], positions[v2Idx * 3 + 1], positions[v2Idx * 3 + 2]);
    const v3 = new Vector3(positions[v3Idx * 3], positions[v3Idx * 3 + 1], positions[v3Idx * 3 + 2]);

    // Get normals for the hit triangle vertices
    const n1 = new Vector3(normals[v1Idx * 3], normals[v1Idx * 3 + 1], normals[v1Idx * 3 + 2]).normalize();
    const n2 = new Vector3(normals[v2Idx * 3], normals[v2Idx * 3 + 1], normals[v2Idx * 3 + 2]).normalize();
    const n3 = new Vector3(normals[v3Idx * 3], normals[v3Idx * 3 + 1], normals[v3Idx * 3 + 2]).normalize();

    // Calculate barycentric coordinates of hit point
    const baryCoords = computeBarycentricCoordinates(hitPoint, v1, v2, v3);

    // Calculate distances from hit point to triangle vertices
    const d1 = Vector3.Distance(hitPoint, v1);
    const d2 = Vector3.Distance(hitPoint, v2);
    const d3 = Vector3.Distance(hitPoint, v3);

    // Calculate average normal at hit point using barycentric weights
    const hitNormal = new Vector3(
        n1.x * baryCoords.u + n2.x * baryCoords.v + n3.x * baryCoords.w,
        n1.y * baryCoords.u + n2.y * baryCoords.v + n3.y * baryCoords.w,
        n1.z * baryCoords.u + n2.z * baryCoords.v + n3.z * baryCoords.w,
    ).normalize();

    // Ensure right and up are perpendicular to the hit normal
    const hitRight = Vector3.Cross(initialUp, hitNormal).normalize();
    const hitUp = Vector3.Cross(hitNormal, hitRight).normalize();

    // Set up initial vertices
    vertexDistances[v1Idx] = d1;
    vertexDistances[v2Idx] = d2;
    vertexDistances[v3Idx] = d3;

    // Calculate geodesic coordinates for initial triangle vertices
    const v1Disp = v1.subtract(hitPoint);
    const v2Disp = v2.subtract(hitPoint);
    const v3Disp = v3.subtract(hitPoint);

    const v1GeoCoord = {
        u: Vector3.Dot(v1Disp, hitRight),
        v: Vector3.Dot(v1Disp, hitUp),
    };

    const v2GeoCoord = {
        u: Vector3.Dot(v2Disp, hitRight),
        v: Vector3.Dot(v2Disp, hitUp),
    };

    const v3GeoCoord = {
        u: Vector3.Dot(v3Disp, hitRight),
        v: Vector3.Dot(v3Disp, hitUp),
    };

    // Store geodesic data for initial vertices
    vertexGeoData[v1Idx] = new GeodesicVertex(v1, n1, hitRight, hitUp, v1GeoCoord);
    vertexGeoData[v2Idx] = new GeodesicVertex(v2, n2, hitRight, hitUp, v2GeoCoord);
    vertexGeoData[v3Idx] = new GeodesicVertex(v3, n3, hitRight, hitUp, v3GeoCoord);

    // Priority queue for Dijkstra's algorithm
    const queue = new PriorityQueue((a, b) => b.distance - a.distance);

    // Initial vertices
    queue.enqueue(v1Idx, d1);
    queue.enqueue(v2Idx, d2);
    queue.enqueue(v3Idx, d3);

    // Process queue until empty or max distance reached
    while (!queue.isEmpty()) {
        // Get closest vertex (no sorting needed)
        const { vertexIdx, distance } = queue.dequeue();

        // Skip if we've exceeded max distance
        if (distance > maxDistance) {
            // eslint-disable-next-line no-continue
            continue;
        }

        // Get geodesic data for current vertex
        const currentGeoData = vertexGeoData[vertexIdx];

        // Process adjacent vertices
        const adjacentVertices = Array.from(vertexAdjacency.get(vertexIdx));
        for (let i = 0; i < adjacentVertices.length; i += 1) {
            const adjVertexIdx = adjacentVertices[i];

            // Skip if already processed with shorter distance
            const existingDistance = vertexDistances[adjVertexIdx];
            if (existingDistance <= distance) {
                // eslint-disable-next-line no-continue
                continue;
            }

            // Get adjacent vertex position and normal
            const adjPos = new Vector3(
                positions[adjVertexIdx * 3 + 0],
                positions[adjVertexIdx * 3 + 1],
                positions[adjVertexIdx * 3 + 2],
            );

            const adjNormal = new Vector3(
                normals[adjVertexIdx * 3 + 0],
                normals[adjVertexIdx * 3 + 1],
                normals[adjVertexIdx * 3 + 2],
            ).normalize();

            // Calculate edge length (geodesic distance increment)
            const edgeLength = Vector3.Distance(currentGeoData.position, adjPos);

            // Calculate new total distance
            const newDistance = distance + edgeLength;

            // Skip if already processed with shorter distance
            if (existingDistance <= newDistance) {
                // eslint-disable-next-line no-continue
                continue;
            }

            // Skip if beyond max distance
            if (newDistance > maxDistance) {
                // eslint-disable-next-line no-continue
                continue;
            }

            // Create new basis for adjacent vertex based on surface curvature
            let adjRight;
            let adjUp;

            // Check how much the normal has changed (surface curvature)
            const normalDot = Vector3.Dot(currentGeoData.normal, adjNormal);

            // Always apply correction.
            //
            // Technically this is a tradeoff between numerical instability between
            // cumulative changes and basis not propagating correctly through
            // mesh of difference densities.

            // Surface is curved, rotate basis vectors
            // Calculate rotation from current normal to adjacent normal
            const rotationAxis = Vector3.Cross(currentGeoData.normal, adjNormal).normalize();
            const rotationAngle = Math.acos(Math.max(-1, Math.min(1, normalDot)));

            // Create rotation matrix
            const rotationMatrix = Matrix.RotationAxis(rotationAxis, rotationAngle);

            // Rotate basis vectors
            adjRight = Vector3.TransformNormal(currentGeoData.right, rotationMatrix).normalize();
            adjUp = Vector3.TransformNormal(currentGeoData.up, rotationMatrix).normalize();

            // Ensure orthogonality (might drift due to numerical issues)
            adjUp = Vector3.Cross(adjNormal, adjRight).normalize();
            adjRight = Vector3.Cross(adjUp, adjNormal).normalize();

            // Calculate displacement in local space
            const displacement = adjPos.subtract(currentGeoData.position);

            // Project displacement onto current basis
            const dispRightProj = Vector3.Dot(displacement, currentGeoData.right);
            const dispUpProj = Vector3.Dot(displacement, currentGeoData.up);

            // Calculate new geodesic coordinates by adding projected displacement
            const adjGeoCoord = {
                u: currentGeoData.geoCoord.u + dispRightProj,
                v: currentGeoData.geoCoord.v + dispUpProj,
            };

            // Store geodesic data for adjacent vertex
            vertexGeoData[adjVertexIdx] = new GeodesicVertex(adjPos, adjNormal, adjRight, adjUp, adjGeoCoord);

            // Update distance
            vertexDistances[adjVertexIdx] = newDistance;

            // Add to queue
            if (newDistance <= maxDistance) {
                queue.enqueue(adjVertexIdx, newDistance);
            }
        }
    }

    // Calculate triangle distances
    for (let i = 0; i < indices.length; i += 3) {
        const triangleId = Math.floor(i / 3);

        const idx1 = indices[i + 0];
        const idx2 = indices[i + 1];
        const idx3 = indices[i + 2];

        const td1 = vertexDistances[idx1];
        const td2 = vertexDistances[idx2];
        const td3 = vertexDistances[idx3];

        // Only include triangles where all vertices have valid distances
        if (td1 < Infinity && td2 < Infinity && td3 < Infinity) {
            // Store minimum distance
            triangleDistances[triangleId] = Math.min(td1, td2, td3);
        }
    }

    return {
        vertexGeoData,
        vertexDistances,
        triangleDistances,
    };
}

/**
 * Converts data from welded vertex indices to original mesh indices
 * @param {Map} weldedData - data map using welded vertex indices as keys
 * @param {Object} weldedMeshData - welded mesh data containing oldToNewIndex map
 * @returns {Map} data map using original vertex indices as keys
 */
function unweldVertexData(weldedData, weldedMeshData) {
    const { oldToNewIndex } = weldedMeshData;

    const unweldedData = new Map();

    // Create inverse mapping (welded -> original)
    const newToOldIndices = new Map();

    for (const [oldIdx, newIdx] of oldToNewIndex.entries()) {
        if (!newToOldIndices.has(newIdx)) {
            newToOldIndices.set(newIdx, []);
        }

        newToOldIndices.get(newIdx).push(oldIdx);
    }

    // Map data from welded to original vertices

    for (const [weldedIdx, data] of weldedData.entries()) {
        const originalIndices = newToOldIndices.get(weldedIdx);
        if (originalIndices) {
            // Map data to all original indices that were welded to this vertex

            for (const origIdx of originalIndices) {
                unweldedData.set(origIdx, data);
            }
        }
    }

    return unweldedData;
}

/**
 * Selects triangles to include in the decal based on geodesic distance
 * @param {Map} triangleDistances - map of triangle distances
 * @param {IndicesArray} indices - mesh indices
 * @param {FloatArray} positions - mesh positions
 * @param {FloatArray} normals - mesh normals
 * @param {number} maxDistance - maximum geodesic distance
 * @param {number} maxTriangles - maximum number of triangles
 * @returns {Array} selected triangles
 */
function selectTrianglesForDecal(
    triangleDistances,
    indices,
    positions,
    normals,
    maxDistance,
    maxTriangles,
) {
    // Sort triangles by distance
    const sortedTriangles = Array.from(triangleDistances.entries())
        // eslint-disable-next-line no-unused-vars
        .filter(([_, distance]) => distance <= maxDistance)
        .sort((a, b) => a[1] - b[1])
        .slice(0, maxTriangles)
        .map(([triangleId, distance]) => ({ triangleId, distance }));

    // Selected triangles.
    const selectedTriangles = [];

    // Process each triangle

    for (const { triangleId, distance } of sortedTriangles) {
        // These are original, un-welded indices.
        const idx1 = indices[triangleId * 3 + 0];
        const idx2 = indices[triangleId * 3 + 1];
        const idx3 = indices[triangleId * 3 + 2];

        selectedTriangles.push({
            triangleId,
            vertices: [idx1, idx2, idx3],
            distance,
        });
    }

    return selectedTriangles;
}

/**
 * Calculate UVs from geodesic coordinates
 */
function calculateUv(vertexIdx, vertexGeoData, size, uvScale) {
    const geoData = vertexGeoData.get(vertexIdx);

    let u; let
v;

    if (geoData) {
        // Use geodesic coordinates directly
        u = 0.5 + (geoData.geoCoord.u / size.x) * uvScale;
        v = 0.5 + (geoData.geoCoord.v / size.y) * uvScale;
    } else {
        // Fallback for vertices without geodesic data
        u = 0.5;
        v = 0.5;
    }

    return [1 - u, v];
}

/**
 * Determines if a value is inside a boundary
 */
function isInsideBoundary(value, boundaryValue) {
    if (boundaryValue === 0) {
        return value >= boundaryValue;
    }

    return value <= boundaryValue;
}

/**
 * Calculate intersection factor
 */
function getIntersectionFactor(value1, value2, boundaryValue) {
    return (boundaryValue - value1) / (value2 - value1);
}

/**
 * Interpolates between two Vector3
 */
function interpolateVector3(v1, v2, t) {
    return new Vector3(
        v1.x + (v2.x - v1.x) * t,
        v1.y + (v2.y - v1.y) * t,
        v1.z + (v2.z - v1.z) * t,
    );
}

/**
 * Interpolates between two UV coordinates
 */
function interpolateUv(uv1, uv2, t) {
    return [
        uv1[0] + (uv2[0] - uv1[0]) * t,
        uv1[1] + (uv2[1] - uv1[1]) * t,
    ];
}

/**
 * Triangulates a polygon using fan triangulation
 * (Only works reliably for convex polygons)
 */
function triangulatePolygon(vertices) {
    if (vertices.length < 3) {
        return [];
    }

    const triangles = [];

    // Fan triangulation from first vertex
    for (let i = 1; i < vertices.length - 1; i += 1) {
        triangles.push([
            vertices[0],
            vertices[i],
            vertices[i + 1],
        ]);
    }

    return triangles;
}

/**
 * Clips a polygon against a Uv boundary
 * @param {Array} vertices - Array of vertex data
 * @param {Object} boundary - Boundary to clip against
 * @returns {Array} Clipped vertices
 */
// eslint-disable-next-line no-unused-vars
function clipPolygonByUvBoundary(vertices, boundary) {
    if (vertices.length < 3) {
        return [];
    }

    const result = [];

    const axisIndex = boundary.axis === 'u' ? 0 : 1;
    const boundaryValue = boundary.value;

    const n = vertices.length;

    for (let i = 0; i < n; i += 1) {
        const current = vertices[i];
        const next = vertices[(i + 1) % n];

        const currentInside = isInsideBoundary(current.uv[axisIndex], boundaryValue);
        const nextInside = isInsideBoundary(next.uv[axisIndex], boundaryValue);

        // If current vertex is inside, add it
        if (currentInside) {
            result.push(current);
        }

        // If one is inside and the other is outside, add the intersection
        if (currentInside !== nextInside) {
            const t = getIntersectionFactor(
                current.uv[axisIndex],
                next.uv[axisIndex],
                boundary.value,
            );

            // Interpolate vertex data
            const interpolated = {
                position: interpolateVector3(current.position, next.position, t),
                normal: interpolateVector3(current.normal, next.normal, t).normalize(),
                uv: interpolateUv(current.uv, next.uv, t),
            };

            // Force the exact boundary value
            interpolated.uv[axisIndex] = boundaryValue;

            result.push(interpolated);
        }
    }

    return result;
}

/**
 * Clips a triangle against Uv boundaries to ensure all Uvs are within [0,1] range.
 * @param {Array} vertices - Array of vertex data (position, normal, uv)
 * @returns {Array} Array of new triangles after clipping
 */
// eslint-disable-next-line no-unused-vars
function clipTriangleByUv(vertices) {
    // Ensure we have a triangle
    if (vertices.length !== 3) {
        return [];
    }

    // Define Uv boundaries
    // eslint-disable-next-line no-unused-vars
    const boundaries = [
        { axis: 'u', value: 0 },
        { axis: 'u', value: 1 },
        { axis: 'v', value: 0 },
        { axis: 'v', value: 1 },
    ];

    // eslint-disable-next-line prefer-const
    let clippedVertices = [...vertices];

    // Clip against each boundary
    for (const boundary of boundaries) {
        if (clippedVertices.length < 3) {
            // Not enough vertices to form a triangle.
            return [];
        }

        clippedVertices = clipPolygonByUvBoundary(clippedVertices, boundary);
    }

    // Convert clipped polygon to triangles (if more than 3 vertices)
    return triangulatePolygon(clippedVertices);
}

/**
 * Generates the mesh data for the decal, including positions, normals, and Uvs.
 * @param {Array} selectedTriangles - selected triangles.
 * @param {Map} vertexGeoData - map of vertex geodesic data with basis vectors and coordinates.
 * @param {Map} vertexDistances - map of vertex distances.
 * @param {Vector3} hitPoint - hit point on the mesh.
 * @param {Vector3} size - size of the decal.
 * @param {number} uvScale - Uv scale factor.
 * @param {Object} sourceMeshData - source mesh data.
 * @returns {Object} mesh data.
 */
function generateDecalMeshData(
    selectedTriangles,
    vertexGeoData,
    vertexDistances,
    hitPoint,
    normal,
    up,
    size,
    uvScale,
    sourceMeshData,
) {
    // Arrays for mesh data
    const decalPositions = [];
    const decalNormals = [];
    const decalUvs = [];
    const decalIndices = [];

    let currentIndex = 0;

    // Process selected triangles
    for (const { vertices } of selectedTriangles) {
        const [idx1, idx2, idx3] = vertices;

        // Get the original vertices data
        const originalVertices = [
            {
                originalIdx: idx1,
                position: new Vector3(
                    sourceMeshData.positions[idx1 * 3 + 0],
                    sourceMeshData.positions[idx1 * 3 + 1],
                    sourceMeshData.positions[idx1 * 3 + 2],
                ),
                normal: new Vector3(
                    sourceMeshData.normals[idx1 * 3 + 0],
                    sourceMeshData.normals[idx1 * 3 + 1],
                    sourceMeshData.normals[idx1 * 3 + 2],
                ),
                uv: calculateUv(idx1, vertexGeoData, size, uvScale),
            },
            {
                originalIdx: idx2,
                position: new Vector3(
                    sourceMeshData.positions[idx2 * 3 + 0],
                    sourceMeshData.positions[idx2 * 3 + 1],
                    sourceMeshData.positions[idx2 * 3 + 2],
                ),
                normal: new Vector3(
                    sourceMeshData.normals[idx2 * 3 + 0],
                    sourceMeshData.normals[idx2 * 3 + 1],
                    sourceMeshData.normals[idx2 * 3 + 2],
                ),
                uv: calculateUv(idx2, vertexGeoData, size, uvScale),
            },
            {
                originalIdx: idx3,
                position: new Vector3(
                    sourceMeshData.positions[idx3 * 3 + 0],
                    sourceMeshData.positions[idx3 * 3 + 1],
                    sourceMeshData.positions[idx3 * 3 + 2],
                ),
                normal: new Vector3(
                    sourceMeshData.normals[idx3 * 3 + 0],
                    sourceMeshData.normals[idx3 * 3 + 1],
                    sourceMeshData.normals[idx3 * 3 + 2],
                ),
                uv: calculateUv(idx3, vertexGeoData, size, uvScale),
            },
        ];

        // Calculate triangle normal
        const triangleNormal = Vector3.Cross(
            originalVertices[1].position.subtract(originalVertices[0].position),
            originalVertices[2].position.subtract(originalVertices[0].position),
        ).normalize();

        // Determine if we need to flip winding order
        const flipWinding = Vector3.Dot(triangleNormal, normal) < 0;

        // Clip the triangle by UV boundaries
        const clippedTriangles = clipTriangleByUv(originalVertices);

        // Add the clipped triangles to the mesh data
        for (const triangle of clippedTriangles) {
            for (let i = 0; i < 3; i += 1) {
                const vertex = triangle[flipWinding ? i : (2 - i)];

                // Add vertex data
                decalPositions.push(vertex.position.x, vertex.position.y, vertex.position.z);
                decalNormals.push(vertex.normal.x, vertex.normal.y, vertex.normal.z);
                decalUvs.push(vertex.uv[0], vertex.uv[1]);

                decalIndices.push(currentIndex);

                currentIndex += 1;
            }
        }
    }

    return {
        positions: decalPositions,
        normals: decalNormals,
        uvs: decalUvs,
        indices: decalIndices,
    };
}

/**
 * Creates the final decal mesh.
 * @param {string} name - name of the mesh.
 * @param {Scene} scene - scene.
 * @param {Object} meshData - mesh data.
 * @returns {Mesh} decal mesh.
 */
function createDecalMesh(
    name,
    scene,
    meshData,
) {
    const {
        positions, normals, uvs, indices,
    } = meshData;

    // Create decal mesh
    const decalMesh = new Mesh(name, scene);

    // Create vertex data
    const vertexData = new VertexData();

    vertexData.positions = positions;
    vertexData.normals = normals;
    vertexData.uvs = uvs;
    vertexData.indices = indices;

    // Apply vertex data to mesh
    vertexData.applyToMesh(decalMesh);

    // Ensure proper bounding info
    decalMesh.refreshBoundingInfo(true);

    return decalMesh;
}

/**
 * Creates a visualization of geodesic distances for debugging
 * @param {Map} vertexDistances - map of vertex distances
 * @param {FloatArray} positions - mesh positions
 * @param {Scene} scene - scene
 * @returns {Mesh} visualization mesh
 */
function visualizeGeodesicDistances(
    name,
    sourceMesh,
    vertexDistances,
    positions,
    indices,
    scene,
) {
    // Find max distance for normalization
    let maxDistance = 0;

    for (const distance of vertexDistances.values()) {
        if (distance < Infinity && distance > maxDistance) {
            maxDistance = distance;
        }
    }

    // Create arrays for mesh data
    const visualPositions = [];
    const visualColors = [];

    // Process all vertices with finite distances
    for (let i = 0; i < positions.length / 3; i += 1) {
        // Add position.
        visualPositions.push(
            positions[i * 3 + 0],
            positions[i * 3 + 1],
            positions[i * 3 + 2],
        );

        const distance = vertexDistances[i];

        let normalizedDistance = 1;

        if (distance !== undefined && distance < Infinity) {
            normalizedDistance = distance / maxDistance;

            // Add color based on distance (blue to red gradient).
            visualColors.push(
                normalizedDistance,
                0,
                1 - normalizedDistance,
                1,
            );
        } else {
            visualColors.push(
                1,
                1,
                1,
                1,
            );
        }
    }

    // Create the visualization mesh
    const visualMesh = new Mesh(`${name}-decaldebug`, scene);

    // Create vertex data.
    const vertexData = new VertexData();

    vertexData.positions = visualPositions;
    vertexData.indices = indices;
    vertexData.colors = visualColors;

    vertexData.applyToMesh(visualMesh);

    // Create a material that uses vertex colors
    const material = new StandardMaterial(`${name}-decaldebug`, scene);

    material.disableLighting = true;
    material.emissiveColor = new Color3(1, 1, 1);

    material.backFaceCulling = true;
    material.useVertexColors = true;

    material.pointsCloud = true;
    material.pointSize = 25;

    visualMesh.renderingGroupId = 2;

    material.alpha = 1;

    visualMesh.material = material;
    visualMesh.isPickable = false;

    return visualMesh;
}

/**
 * Calculates a hash value for a given world transform matrix.
 * This can be useful for detecting changes in position, rotation, or scale.
 *
 * @param {BABYLON.Matrix} matrix - The world transform matrix to hash
 * @param {number} precision - Number of decimal places to round to (default: 3)
 * @returns {string} A string hash representing the matrix state
 */
function getWorldMatrixHash(matrix, precision = 6) {
    if (!matrix) {
        return null;
    }

    // Extract the 16 values from the matrix (4x4)
    const values = [
        matrix.m[0], matrix.m[1], matrix.m[2], matrix.m[3],
        matrix.m[4], matrix.m[5], matrix.m[6], matrix.m[7],
        matrix.m[8], matrix.m[9], matrix.m[10], matrix.m[11],
        matrix.m[12], matrix.m[13], matrix.m[14], matrix.m[15],
    ];

    // Round values to the specified precision to prevent minor floating point differences
    const roundedValues = values.map((val) => parseFloat(val.toFixed(precision)));

    // Create a string representation and hash it
    const valuesString = roundedValues.join(',');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < valuesString.length; i += 1) {
        const char = valuesString.charCodeAt(i);

        // eslint-disable-next-line no-bitwise
        hash = ((hash << 5) - hash) + char;

        // eslint-disable-next-line no-bitwise
        hash &= hash;
    }

    return hash.toString(16); // Return as hex string
}

const topologyCache = {};

/**
 * Creates a decal mesh that conforms to curved surfaces by calculating geodesic distances.
 * @param {string} name - name of the mesh
 * @param {AbstractMesh} sourceMesh - mesh where the decal must be applied
 * @param {Vector3} position - position where to create the decal (in world space)
 * @param {Vector3} normal - normal of the decal (in world space)
 * @param {Vector3} size - size of the decal
 * @param {Vector3} up - up vector of the decal (in world space) - to orient the decal
 * @param {Object} options - additional options for creating the decal
 * @returns {Mesh} the decal mesh
 */
// eslint-disable-next-line import/prefer-default-export
export function CreateConformingDecal(
    name,
    sourceMesh,
    position,
    normal,
    size,
    up,
    options = {},
) {
    // Default options
    const maxGeodesicDistance = options.maxGeodesicDistance || Math.abs(size.x + size.y) / 2;

    const uvScale = options.uvScale || 1.0;
    const maxTriangles = options.maxTriangles || 100000;
    const debugMode = options.debugMode || false;

    const localToWorld = sourceMesh.getWorldMatrix().clone();

    // Find intersection with mesh.
    // Ray is in WORLD space.
    const rayOffset = normal.scale(size.z * 0.5);
    const rayOrigin = position.add(rayOffset);

    const ray = new Ray(rayOrigin, normal.scale(-1));
    const pickResult = ray.intersectsMesh(sourceMesh);

    if (!pickResult.hit) {
        // eslint-disable-next-line
        console.warn(`No intersection found for decal at position ${position}`);

        // Return empty mesh.
        return { decal: new Mesh(name, sourceMesh.getScene()) };
    }

    // Hitpoint is in world coordinates.
    const hitPoint = pickResult.pickedPoint;
    const hitFaceId = pickResult.faceId;

    // Extract mesh data
    const indices = sourceMesh.getIndices();
    const localPositions = sourceMesh.getVerticesData(VertexBuffer.PositionKind);
    const localNormals = sourceMesh.getVerticesData(VertexBuffer.NormalKind);
    const uvs = sourceMesh.getVerticesData(VertexBuffer.UVKind);

    if (!indices || !localPositions || !localNormals) {
        // eslint-disable-next-line
        console.error('Mesh is missing required data for creating a decal');

        return { decal: new Mesh(name, sourceMesh.getScene()) };
    }

    // Get the face normal at the intersection point
    const faceId = pickResult.faceId;

    // Get the vertices of the face
    const v1 = indices[faceId * 3 + 0];
    const v2 = indices[faceId * 3 + 1];
    const v3 = indices[faceId * 3 + 2];

    // Get the normal of the face (average of vertex normals)
    const localFaceNormal = new Vector3(
        (localNormals[v1 * 3 + 0] + localNormals[v2 * 3 + 0] + localNormals[v3 * 3 + 0]) / 3,
        (localNormals[v1 * 3 + 1] + localNormals[v2 * 3 + 1] + localNormals[v3 * 3 + 1]) / 3,
        (localNormals[v1 * 3 + 2] + localNormals[v2 * 3 + 2] + localNormals[v3 * 3 + 2]) / 3,
    ).normalize();

    // Transform the normal to world space
    const worldFaceNormal = Vector3.TransformNormal(
        localFaceNormal,
        localToWorld,
    );

    const hitDotProduct = Vector3.Dot(ray.direction, worldFaceNormal);

    const isFacingAway = hitDotProduct > 0;

    if (isFacingAway) {
        // eslint-disable-next-line no-console
        console.log('Hit a face facing away from ray origin');

        return { decal: new Mesh(name, sourceMesh.getScene()) };
    }

    const hash = `${sourceMesh.uniqueId}-${getWorldMatrixHash(localToWorld)}`;

    let positions = null;
    let normals = null;
    let weldedMeshData = null;
    let weldedPositions = null;
    let weldedNormals = null;
    let weldedIndices = null;
    let topology = null;

    if (topologyCache[sourceMesh.uniqueId] && topologyCache[sourceMesh.uniqueId][hash]) {
        positions = topologyCache[sourceMesh.uniqueId][hash].positions;
        normals = topologyCache[sourceMesh.uniqueId][hash].normals;
        weldedMeshData = topologyCache[sourceMesh.uniqueId][hash].weldedMeshData;
        weldedPositions = topologyCache[sourceMesh.uniqueId][hash].weldedPositions;
        weldedNormals = topologyCache[sourceMesh.uniqueId][hash].weldedNormals;
        weldedIndices = topologyCache[sourceMesh.uniqueId][hash].weldedIndices;
        topology = topologyCache[sourceMesh.uniqueId][hash].topology;
    } else {
        positions = new Float32Array(localPositions.length);
        for (let i = 0; i < localPositions.length; i += 3) {
            const p = Vector3.TransformCoordinates(new Vector3(localPositions[i + 0], localPositions[i + 1], localPositions[i + 2]), localToWorld);
            positions[i + 0] = p.x;
            positions[i + 1] = p.y;
            positions[i + 2] = p.z;
        }

        normals = new Float32Array(localNormals.length);
        for (let i = 0; i < localNormals.length; i += 3) {
            const n = Vector3.TransformNormal(new Vector3(localNormals[i + 0], localNormals[i + 1], localNormals[i + 2]), localToWorld);
            normals[i + 0] = n.x;
            normals[i + 1] = n.y;
            normals[i + 2] = n.z;
        }

        weldedMeshData = weldVertices(positions, indices);
        weldedPositions = weldedMeshData.positions;
        weldedIndices = weldedMeshData.indices;

        weldedNormals = weldVertexAttributes(normals, weldedMeshData, 3);

        topology = buildMeshTopology(weldedIndices, weldedPositions);

        topologyCache[sourceMesh.uniqueId] = {};

        topologyCache[sourceMesh.uniqueId][hash] = {
            positions,
            normals,
            weldedMeshData,
            weldedPositions,
            weldedNormals,
            weldedIndices,
            topology,
        };
    }

    // Calculate basis vectors
    const right = Vector3.Cross(up, normal).normalize();
    const adjustedUp = Vector3.Cross(normal, right).normalize();

    // Compute geodesic basis and coordinates
    const {
        vertexGeoData: weldedVertexGeoData,
        vertexDistances: weldedVertexDistances,
        triangleDistances,
    } = computeGeodesicBasis(
        hitPoint,
        hitFaceId,
        weldedIndices,
        weldedPositions,
        weldedNormals,
        topology,
        right,
        adjustedUp,
        maxGeodesicDistance,
    );

    // Unweld the geodesic data
    const vertexGeoData = unweldVertexData(weldedVertexGeoData, weldedMeshData);
    const vertexDistances = unweldVertexData(weldedVertexDistances, weldedMeshData);

    // Select triangles for the decal.
    const selectedTriangles = selectTrianglesForDecal(
        triangleDistances,
        indices,
        positions,
        normals,
        maxGeodesicDistance,
        maxTriangles,
    );

    // Generate UV coordinates for the decal.
    const decalData = generateDecalMeshData(
        selectedTriangles,
        vertexGeoData,
        vertexDistances,
        hitPoint,
        normal,
        up,
        size,
        uvScale,
        {
            positions,
            normals,
            uvs,
        },
    );

    // Create the decal mesh

    const decalMesh = createDecalMesh(
        name,
        sourceMesh.getScene(),
        decalData,
    );

    // Optional debug visualization
    let debugMesh = null;

    if (debugMode) {
        debugMesh = visualizeGeodesicDistances(
            name,
            sourceMesh,
            weldedVertexDistances,
            weldedPositions,
            weldedIndices,
            sourceMesh.getScene(),
        );
    }

    return { decal: decalMesh, debugMesh };
}
